import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { QrCode, Download, Copy, Printer, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { QR } from "@/constants/testIds/app";

/**
 * RecenUp QR Code section.
 * Renders a preview + download PNG/SVG + copy link + print.
 * The QR encodes the same URL as the NFC card (with ?src=qr for future analytics).
 */
export default function QrCodeSection({ business }) {
  const cardId = business.card_id;
  const isActive = business.card_status === "active";

  const url = cardId
    ? `${window.location.origin}/r/${cardId}?src=qr`
    : "";

  const [pngDataUrl, setPngDataUrl] = useState("");
  const [svgString, setSvgString] = useState("");
  const [copied, setCopied] = useState(false);
  const printFrameRef = useRef(null);

  useEffect(() => {
    if (!url) {
      setPngDataUrl("");
      setSvgString("");
      return;
    }
    // Generate PNG data URL
    QRCode.toDataURL(url, {
      errorCorrectionLevel: "M",
      margin: 2,
      width: 512,
      color: { dark: "#0F0F10", light: "#FFFFFF" },
    })
      .then(setPngDataUrl)
      .catch(() => setPngDataUrl(""));

    // Generate SVG string
    QRCode.toString(url, {
      errorCorrectionLevel: "M",
      type: "svg",
      margin: 2,
      color: { dark: "#0F0F10", light: "#FFFFFF" },
    })
      .then(setSvgString)
      .catch(() => setSvgString(""));
  }, [url]);

  const filenameBase = `recenup-${cardId || "qr"}-${(business.name || "attivita")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}`;

  const downloadPng = () => {
    if (!pngDataUrl) return;
    const a = document.createElement("a");
    a.href = pngDataUrl;
    a.download = `${filenameBase}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadSvg = () => {
    if (!svgString) return;
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `${filenameBase}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const copyUrl = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const printQr = () => {
    if (!svgString) return;
    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>RecenUp — ${business.name || "QR Code"}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; font-family: 'Manrope', -apple-system, sans-serif; color: #0F0F10; background: #ffffff; }
    .page { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 32px; }
    .card { border: 1px solid #EDECE7; border-radius: 24px; padding: 48px; text-align: center; max-width: 480px; width: 100%; }
    .brand { font-family: Georgia, 'Instrument Serif', serif; font-size: 28px; letter-spacing: -0.01em; }
    .brand em { color: #00F2F4; font-style: italic; }
    .biz { font-family: Georgia, 'Instrument Serif', serif; font-size: 34px; letter-spacing: -0.01em; margin: 20px 0 6px; }
    .addr { font-size: 12px; color: #6B6B72; margin-bottom: 24px; }
    .qr { display: inline-block; }
    .qr svg { width: 320px; height: 320px; display: block; }
    .cta { margin-top: 28px; font-size: 14px; color: #0F0F10; }
    .cta strong { color: #00F2F4; }
    .id { margin-top: 24px; font-size: 10px; letter-spacing: 0.25em; text-transform: uppercase; color: #A3A3AB; }
    @media print { .page { padding: 0; } .card { border: none; } }
  </style>
</head>
<body>
  <div class="page">
    <div class="card">
      <div class="brand">Recen<em>Up</em></div>
      <div class="biz">${(business.name || "").replace(/</g, "&lt;")}</div>
      ${business.address ? `<div class="addr">${business.address.replace(/</g, "&lt;")}</div>` : ""}
      <div class="qr">${svgString}</div>
      <div class="cta">Scansiona e <strong>lascia una recensione</strong> su Google</div>
      <div class="id">Card ${cardId || ""}</div>
    </div>
  </div>
  <script>window.onload = () => { window.focus(); window.print(); };</script>
</body>
</html>`;
    const blob = new Blob([html], { type: "text/html" });
    const objectUrl = URL.createObjectURL(blob);
    const w = window.open(objectUrl, "_blank");
    if (!w) toast.error("Il browser ha bloccato la finestra di stampa");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
  };

  return (
    <div
      data-testid={QR.section(business.business_id)}
      className="mt-8 pt-8 border-t border-[var(--border-c)]"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-1)]">
            <QrCode size={17} strokeWidth={1.7} />
          </div>
          <div>
            <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
              QR Code · {business.name}
            </div>
            <div className="mt-0.5 font-serif text-xl text-[var(--fg-1)] tracking-tight leading-tight">
              QR Code dell'attività
            </div>
          </div>
        </div>
        <span
          data-testid={QR.statusBadge(business.business_id)}
          className={`inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${
            isActive
              ? "bg-[#4A8A6E]/12 text-[#3E7A5E]"
              : "bg-[#C6423B]/12 text-[#C6423B]"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-[#4A8A6E] animate-pulse" : "bg-[#C6423B]"}`} />
          {isActive ? "Attivo" : "Non attivo"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-start">
        {/* Preview */}
        <div
          data-testid={QR.preview(business.business_id)}
          className="mx-auto md:mx-0 p-5 rounded-2xl bg-white border border-[var(--border-c)] shadow-[0_2px_12px_rgba(15,15,16,0.04)]"
        >
          {pngDataUrl ? (
            <img
              src={pngDataUrl}
              alt={`QR code per ${business.name}`}
              className="block h-44 w-44 md:h-48 md:w-48"
            />
          ) : (
            <div className="h-44 w-44 md:h-48 md:w-48 flex items-center justify-center text-xs text-[var(--fg-4)]">
              {isActive ? "Generazione..." : "Nessuna card attiva"}
            </div>
          )}
        </div>

        {/* Info + actions */}
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)] mb-2">
              Link associato
            </div>
            <div
              data-testid={QR.urlDisplay(business.business_id)}
              className="p-3 rounded-lg bg-[var(--bg-3)] border border-[var(--border-c)] font-mono text-xs text-[var(--fg-2)] break-all"
            >
              {url || <span className="text-[var(--fg-4)]">Nessuna card associata</span>}
            </div>
            <p className="mt-3 text-xs text-[var(--fg-3)] leading-relaxed">
              Il QR punta allo stesso link della card NFC. Ogni scansione viene registrata e conteggiata nelle statistiche.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              data-testid={QR.downloadPngBtn(business.business_id)}
              onClick={downloadPng}
              disabled={!pngDataUrl}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-4 py-2.5 text-sm font-sans disabled:opacity-40"
              style={{ transition: "background-color 0.2s ease" }}
            >
              <Download size={14} /> Scarica PNG
            </button>
            <button
              data-testid={QR.downloadSvgBtn(business.business_id)}
              onClick={downloadSvg}
              disabled={!svgString}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-4 py-2.5 text-sm font-sans disabled:opacity-40"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <Download size={14} /> Scarica SVG
            </button>
            <button
              data-testid={QR.copyBtn(business.business_id)}
              onClick={copyUrl}
              disabled={!url}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-4 py-2.5 text-sm font-sans disabled:opacity-40"
              style={{ transition: "border-color 0.2s ease" }}
            >
              {copied ? (
                <>
                  <CheckCircle2 size={14} className="text-[#4A8A6E]" /> Copiato
                </>
              ) : (
                <>
                  <Copy size={14} /> Copia link
                </>
              )}
            </button>
            <button
              data-testid={QR.printBtn(business.business_id)}
              onClick={printQr}
              disabled={!svgString}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-4 py-2.5 text-sm font-sans disabled:opacity-40"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <Printer size={14} /> Stampa QR Code
            </button>
          </div>
        </div>
      </div>

      <iframe ref={printFrameRef} title="print" className="hidden" />
    </div>
  );
}
